import constate from 'constate';
import { useCallback, useEffect, useRef, useState } from 'react';

const EVENT_NEW_TAB = 'newTabOpened';
const CHANNEL_NAME = 'color-multi-tab-detector';

// Singleton instance for non-React usage (like in actions.ts)
const globalMultiTabState = { hasMultipleTabs: false };

// Function to update the global state (called by the provider)
const updateGlobalMultiTabState = (hasMultipleTabs: boolean): void => {
  globalMultiTabState.hasMultipleTabs = hasMultipleTabs;
};

interface IMultiTabDetectorContext {
  hasMultipleTabs: boolean;
  reset: () => void;
}

function useMultiTabDetector(): IMultiTabDetectorContext {
  const [hasMultipleTabs, setHasMultipleTabs] = useState(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const initializeBroadcastChannel = useCallback((): void => {
    try {
      if (typeof BroadcastChannel === 'undefined') {
        return; // BroadcastChannel not supported
      }
      const channel = new BroadcastChannel(CHANNEL_NAME);
      broadcastChannelRef.current = channel;

      // Setup message handler
      channel.onmessage = (event): void => {
        if (event.data === EVENT_NEW_TAB) {
          setHasMultipleTabs(true);
          updateGlobalMultiTabState(true);
        }
      };

      // Announce this tab's existence
      channel.postMessage(EVENT_NEW_TAB);
    } catch {
      // BroadcastChannel failed to initialize, continue without it
    }
  }, []);

  const destroy = useCallback((): void => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.close();
      broadcastChannelRef.current = null;
    }
  }, []);

  const reset = useCallback((): void => {
    setHasMultipleTabs(false);
    updateGlobalMultiTabState(false);
  }, []);

  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    initializeBroadcastChannel();
    return destroy;
  }, [initializeBroadcastChannel, destroy]);

  return {
    hasMultipleTabs,
    reset,
  };
}

const dest = constate(useMultiTabDetector);
export const MultiTabDetectorProvider: React.FC<React.PropsWithChildren<object>> = dest[0];
export const useMultiTabDetectorContext: () => IMultiTabDetectorContext = dest[1];

// Function to check multiple tabs from outside React components
export const hasMultipleTabsOpen = (): boolean => {
  return globalMultiTabState.hasMultipleTabs;
};
